/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Appeal, Complaints, Investigation } from "types";
import { format } from "date-fns";
import {
  fetchAdvocateLogsByAppealId,
  fetchAdvocateLogsByComplaintId,
  fetchAdvocateLogsByInvestigationId,
} from "services/advocateComplaintService";
import { useTranslation } from "contexts/i18nContext";

interface LogEntry {
  uu_id: string;
  model_name: string;
  instance_id: string;
  action_type: string;
  field_name: string;
  old_value: string;
  new_value: string;
  changed_by: string;
  user_id_changed_by: string;
  changed_at: string;
  additional_info: string;
  org_id: string;
  admin_unit_id: string;
}

interface Props {
  complaintData: Complaints;
  investigationData?: Investigation;
  appealData?: Appeal | null;
}

export default function ActivityLogs({
  complaintData,
  investigationData,
  appealData,
}: Props) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [logsData, setLogsData] = useState<LogEntry[]>([]);
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState("");

  useEffect(() => {
    if (!complaintData && !Array.isArray(complaintData)) return;

    // setLoading(true);
    const loadComplaintLogs = async () => {
      try {
        const data = await fetchAdvocateLogsByComplaintId(complaintData?.uu_id);
        setLogsData((prev) => [...prev, ...data]);
        // setError("");
      } catch (err) {
        // setError("Failed to load logs.");
      } finally {
        // setLoading(false);
      }
    };
    loadComplaintLogs();
  }, []);

  useEffect(() => {
    if (!investigationData) return;

    // setLoading(true);
    const loadInvestigationLogs = async () => {
      try {
        const data = await fetchAdvocateLogsByInvestigationId(
          investigationData?.uu_id,
        );
        setLogsData((prev) => [...prev, ...data]);
        // setError("");
      } catch (err) {
        // setError("Failed to load logs.");
      } finally {
        // setLoading(false);
      }
    };
    loadInvestigationLogs();
  }, []);

  useEffect(() => {
    if (!appealData) return;

    // setLoading(true);
    const loadAppealLogs = async () => {
      try {
        const data = await fetchAdvocateLogsByAppealId(appealData?.id);
        setLogsData((prev) => [...prev, ...data]);
        // setError("");
      } catch (err) {
        // setError("Failed to load logs.");
      } finally {
        // setLoading(false);
      }
    };
    loadAppealLogs();
  }, []);

  const toggleLogs = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="bg-gray-700 rounded-xl p-5 shadow space-y-4">
      <h3
        className="text-base font-semibold text-white border-b pb-3 cursor-pointer flex justify-between items-center"
        onClick={toggleLogs}
      >
        {t("activityLogs")}
        <span className="text-sm text-gray-800 bg-white px-3 py-1 rounded-full hover:bg-gray-200 transition">
          {isExpanded ? t("hide") + " ▲" : t("show") + " ▼"}
        </span>
      </h3>

      {isExpanded && (
        <div className="max-h-96 overflow-y-auto pr-1 space-y-3">
          {logsData.map((log) => {
            const dateObj = new Date(log.changed_at);
            return (
              <div
                key={log.uu_id}
                className="bg-white p-4 rounded-xl shadow-sm flex items-start gap-4"
              >
                <div className="text-center w-14">
                  <p className="text-sm text-gray-500">
                    {format(dateObj, "MMM")}
                  </p>
                  <p className="text-2xl font-bold leading-none">
                    {dateObj.getDate()}
                  </p>
                  <p className="text-sm text-gray-500">
                    {dateObj.getFullYear()}
                  </p>
                </div>
                <div className="flex-1 space-y-1">
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                      {t("approved")}
                    </span>
                  </div>
                  <p className="font-semibold text-gray-800">
                    {log.changed_by}
                  </p>
                  <p className="text-sm text-gray-600">
                    {format(dateObj, "dd MMMM yyyy - h:mmaaa")}
                  </p>
                  <p className="text-sm text-gray-700">{log.additional_info}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
